<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div class="chart-card__title-group">
        <h3 class="chart-card__title">{{ title }}</h3>
        <span v-if="subtitle" class="chart-card__subtitle">{{ subtitle }}</span>
      </div>
      <div v-if="peakHour" class="chart-card__peak">
        <font-awesome-icon icon="fire" class="chart-card__peak-icon" />
        Peak: {{ peakHour }}
      </div>
    </div>
    <!-- Day selector buttons (oldest to newest, left to right) -->
    <div v-if="hasMultipleDays" class="chart-card__day-selector">
      <button
        v-for="(day, index) in reversedHistoricalData"
        :key="index"
        :class="['chart-card__day-btn', { 'chart-card__day-btn--active': selectedDayIndex === day.originalIndex }]"
        :style="selectedDayIndex === day.originalIndex ? { backgroundColor: dayColors[day.originalIndex], borderColor: dayColors[day.originalIndex] } : {}"
        @click="selectedDayIndex = day.originalIndex"
      >
        {{ day.name }}
      </button>
    </div>
    <div class="chart-card__body">
      <apexchart
        v-if="hasData"
        :type="chartType"
        :height="height"
        :options="chartOptions"
        :series="chartSeries"
        :key="selectedDayIndex"
      />
      <div v-else class="chart-card__empty">
        <font-awesome-icon icon="clock" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'HourlySalesChart',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return {
      selectedDayIndex: 0
    };
  },
  props: {
    title: {
      type: String,
      default: 'Salg per time'
    },
    subtitle: {
      type: String,
      default: ''
    },
    labels: {
      type: Array,
      default: () => ['08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22']
    },
    data: {
      type: Array,
      default: () => []
    },
    historicalData: {
      type: Array,
      default: () => []
    },
    height: {
      type: Number,
      default: 280
    }
  },
  computed: {
    hasMultipleDays() {
      return this.historicalData && this.historicalData.length > 0;
    },
    reversedHistoricalData() {
      // Reverse order so oldest is on the left, "I dag" on the right
      return this.historicalData
        .map((day, index) => ({ ...day, originalIndex: index }))
        .slice()
        .reverse();
    },
    chartType() {
      return 'bar';
    },
    hasData() {
      if (this.hasMultipleDays) {
        return this.historicalData.some(day => day.data && day.data.some(v => v > 0));
      }
      return this.data && this.data.length > 0 && this.data.some(v => v > 0);
    },
    selectedDayData() {
      if (this.hasMultipleDays) {
        return this.historicalData[this.selectedDayIndex]?.data || [];
      }
      return this.data;
    },
    selectedDayName() {
      if (this.hasMultipleDays) {
        return this.historicalData[this.selectedDayIndex]?.name || '';
      }
      return 'I dag';
    },
    peakHourIndex() {
      const dayData = this.selectedDayData;
      if (!dayData || dayData.length === 0 || !dayData.some(v => v > 0)) return -1;
      return dayData.indexOf(Math.max(...dayData));
    },
    peakHour() {
      if (this.peakHourIndex < 0) return null;
      return this.labels[this.peakHourIndex] + ':00';
    },
    chartSeries() {
      return [{
        name: 'Salg',
        data: this.selectedDayData
      }];
    },
    chartOptions() {
      return this.selectedDayChartOptions;
    },
    dayColors() {
      return [
        '#B30E10',  // I dag - Red (primary)
        '#6366f1',  // I går - Indigo
        '#10b981',  // 2 dager siden
        '#f59e0b',  // 3 dager siden
        '#ec4899',  // 4 dager siden
        '#8b5cf6',  // 5 dager siden
        '#06b6d4',  // 6 dager siden
        '#84cc16'   // 7 dager siden
      ];
    },
    selectedDayChartOptions() {
      const selectedColor = this.hasMultipleDays
        ? this.dayColors[this.selectedDayIndex]
        : '#B30E10';
      const maxValue = Math.max(...this.selectedDayData);

      // Create colors array - peak bar gets full color, others get lighter version
      const barColors = this.selectedDayData.map(val =>
        val === maxValue && maxValue > 0 ? selectedColor : this.lightenColor(selectedColor, 0.6)
      );

      return {
        chart: {
          type: 'bar',
          fontFamily: 'inherit',
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 300
          }
        },
        colors: barColors,
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '70%',
            distributed: true
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.labels.map(h => h + ':00'),
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '10px'
            },
            rotate: 0,
            formatter: (val, index) => {
              // Show every 2 hours: 00:00, 02:00, 04:00, etc.
              const hour = parseInt(val);
              if (hour % 2 === 0) {
                return val;
              }
              return '';
            }
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '11px'
            },
            formatter: (val) => {
              if (val >= 1000) {
                return (val / 1000).toFixed(0) + 'k';
              }
              return val ? val.toFixed(0) : '0';
            }
          }
        },
        grid: {
          borderColor: 'var(--bo-surface-2)',
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: false
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => {
              if (val === null || val === undefined) return '0 kr';
              return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
            }
          }
        }
      };
    }
  },
  methods: {
    lightenColor(hex, factor) {
      // Convert hex to RGB, lighten, and convert back
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);

      const newR = Math.round(r + (255 - r) * factor);
      const newG = Math.round(g + (255 - g) * factor);
      const newB = Math.round(b + (255 - b) * factor);

      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.chart-card__title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0;
}

.chart-card__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: var(--bo-muted);
}

.chart-card__peak {
  font-size: 13px;
  font-weight: 600;
  color: var(--bo-alert-ink);
  background: var(--bo-bad-bg);
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-card__peak-icon {
  color: #ff6b35;
}

.chart-card__body {
  min-height: 200px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.chart-card__day-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.chart-card__day-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  background: var(--bo-surface);
  color: var(--bo-muted);
  cursor: pointer;
  transition: all 0.15s ease;
}

.chart-card__day-btn:hover {
  border-color: var(--bo-muted-2);
  background: var(--bo-surface-2);
}

.chart-card__day-btn--active {
  color: var(--bo-surface);
  border-color: transparent;
}

.chart-card__day-btn--active:hover {
  opacity: 0.9;
}
</style>
